import React, { useContext, useEffect, useRef, useState } from 'react';
import { ThemeProvider } from 'emotion-theming';
import { Theme, ThemeContext } from '../../utilities/theme';
import useClickOutside from '../../hooks/useClickOutside';
import { ColorCodes, colorsForTheme } from '../../foundations/color';
import Icon from '../../foundations/Icons';
import HeaderDropdown from '../HeaderNavigation/HeaderDropdown';
import {
  IconWrapper,
  ContentWrapper,
  AccountDropdownWrapper,
  AccountSummaryWrapper,
  TextWrapper,
} from './AccountDropdown.styles';
import AccountSummaryItem from './AccountSummaryItem';
import RetailerSwitcher from './RetailerSwitcher';
import Divider from '../../components/Divider';
import SupplierSwitcher from './SupplierSwitcher';

export type AccountDropdownProps = {
  params?: { retailerId?: string; supplierId?: string };
  retailers?: string[];
  suppliers?: string[];
  children: React.ReactNode;
  logout?: Function;
  fullName: string;
  email?: string;
  context?: 'supplier' | 'retailer' | 'dashboard';
};

const getTheme = (theme: Theme): themeStyles => {
  const colors = colorsForTheme(theme);
  const colorThemes = {
    backgroundColor: colors.SWAN,
    anchorColor: colors.ASH,
    activeColor: colors.PEACH,
    accountBackgroundColor: colors.ASH,
    accountCircleColor: colors.SWAN,
    textColor: colors.SPACE,
    textHoverColor: colors.SWEET_ORANGE,
    textHoverBGColor: colors.LIGHT_PEACH,
    nameColor: colors.SPACE,
    emailColor: colors.STORM,
    circleInitialsColor: colors.STEEL,
  };
  return colorThemes;
};

type themeStyles = {
  backgroundColor: ColorCodes;
  anchorColor: ColorCodes;
  activeColor: ColorCodes;
  accountBackgroundColor: ColorCodes;
  accountCircleColor: ColorCodes;
  textColor: ColorCodes;
  textHoverColor: ColorCodes;
  textHoverBGColor: ColorCodes;
  nameColor: ColorCodes;
  emailColor: ColorCodes;
  circleInitialsColor: ColorCodes;
};

export const AccountDropdown = (props: AccountDropdownProps) => {
  const {
    params,
    children,
    logout,
    fullName,
    email,
    retailers = [],
    suppliers = [],
    context,
  } = props;
  const [isActive, setIsActive] = useState(false);
  const themeStyle = getTheme(useContext(ThemeContext));
  const handleClickOutside = () => {
    setIsActive(false);
  };
  const ref = useRef<HTMLDivElement>(null);
  useClickOutside(ref, handleClickOutside);

  const retailerRef = useRef<HTMLDivElement>(null);
  const supplierRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isActive) {
      if (retailerRef.current) {
        retailerRef.current.scrollTop = 0;
      }
      if (supplierRef.current) {
        supplierRef.current.scrollTop = 0;
      }
    }
  }, [isActive]);
  return (
    <ThemeProvider theme={themeStyle}>
      <AccountDropdownWrapper
        data-testid="account-dropdown-menu"
        isActive={isActive}
        onClick={() => setIsActive(!isActive)}
        ref={ref}
      >
        <IconWrapper>
          <Icon name="lUser" />
        </IconWrapper>
        <HeaderDropdown
          isActive={isActive}
          anchor="right"
          anchorColor={themeStyle.anchorColor}
        >
          <ContentWrapper>
            <AccountSummaryWrapper>
              <AccountSummaryItem fullName={fullName} email={email} />
            </AccountSummaryWrapper>
            {suppliers?.length > 0 && (
              <>
                <SupplierSwitcher
                  params={params}
                  suppliers={suppliers}
                  external={context !== 'supplier'}
                  showHeader={retailers.length > 0}
                  ref={supplierRef}
                />
                <Divider type="subtle" />
              </>
            )}
            {retailers?.length > 0 && (
              <>
                <RetailerSwitcher
                  params={params}
                  retailers={retailers}
                  external={context !== 'dashboard'}
                  context={context}
                  showHeader={suppliers.length > 0}
                  ref={retailerRef}
                />
                <Divider type="subtle" />
              </>
            )}
            {children}
            <TextWrapper
              data-testid="logout-button"
              onClick={() => {
                if (logout) logout();
              }}
            >
              Log out
            </TextWrapper>
          </ContentWrapper>
        </HeaderDropdown>
      </AccountDropdownWrapper>
    </ThemeProvider>
  );
};

export default AccountDropdown;
